import React, { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Play,
  CheckCircle,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Camera,
  Flag,
  Wrench,
  Shield,
  ShieldAlert,
  Loader2,
  Save,
  Edit2,
  Sparkles,
  Trash2,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  FileText,
  Cpu,
  Layers,
  Award,
  X,
  Check,
  RotateCcw,
  Lock,
} from "lucide-react";
import { sopsAPI, maintenanceAPI } from "../api";
import { useAuthStore } from "../store/authStore";
import clsx from "clsx";
import toast from "react-hot-toast";

const statusConfig = {
  approved: {
    label: "Approved",
    badge: "bg-emerald-50 text-emerald-700 border-emerald-200/80",
    dot: "bg-emerald-500",
  },
  pending_review: {
    label: "Pending Review",
    badge: "bg-amber-50 text-amber-700 border-amber-200/80",
    dot: "bg-amber-500",
  },
  draft: {
    label: "Draft",
    badge: "bg-slate-100 text-slate-700 border-slate-200",
    dot: "bg-slate-400",
  },
  deprecated: {
    label: "Deprecated",
    badge: "bg-rose-50 text-rose-700 border-rose-200/80",
    dot: "bg-rose-500",
  },
};

const categoryOptions = {
  Operation: { label: "Operation", dot: "bg-blue-500" },
  Maintenance: { label: "Maintenance", dot: "bg-amber-500" },
  Safety: { label: "Safety", dot: "bg-rose-500" },
  Troubleshooting: { label: "Troubleshooting", dot: "bg-purple-500" },
};

const skillLevelOptions = {
  Basic: { label: "Basic", sublabel: "Standard Operator", dot: "bg-emerald-500" },
  Intermediate: { label: "Intermediate", sublabel: "Trained Tech", dot: "bg-blue-500" },
  Advanced: { label: "Advanced", sublabel: "Certified Engineer", dot: "bg-amber-500" },
  Expert: { label: "Expert", sublabel: "OEM Specialist", dot: "bg-purple-500" },
};

const getCategoryConfig = (cat) => {
  if (!cat) return categoryOptions.Operation;
  const match = Object.keys(categoryOptions).find(
    (k) => k.toLowerCase() === cat.toLowerCase()
  );
  return match ? categoryOptions[match] : { label: cat, dot: "bg-blue-500" };
};

const getSkillConfig = (skill) => {
  if (!skill) return skillLevelOptions.Intermediate;
  const match = Object.keys(skillLevelOptions).find(
    (k) => k.toLowerCase() === skill.toLowerCase()
  );
  return match ? skillLevelOptions[match] : { label: skill, dot: "bg-blue-500" };
};

const SOPViewer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  // State Management
  const [isExecuting, setIsExecuting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState({});
  const [notes, setNotes] = useState({});
  const [finalSummary, setFinalSummary] = useState("");
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [editData, setEditData] = useState(null);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isSkillOpen, setIsSkillOpen] = useState(false);
  const categoryRef = useRef(null);
  const skillRef = useRef(null);

  const { data: sop, isLoading } = useQuery({
    queryKey: ["sop", id],
    queryFn: () => sopsAPI.getById(id).then((res) => res.data.data),
  });

  const userRole = user?.role?.toLowerCase() || "";
  const isAdmin = userRole === "super_admin" || userRole === "admin";
  const sopStatus = sop?.status?.toLowerCase() || "draft";
  const isDraft = sopStatus === "draft";
  const currentStatus = statusConfig[sopStatus] || statusConfig.draft;

  useEffect(() => {
    if (sop && !editData) {
      setEditData({
        category: sop.category || "Operation",
        estimated_duration: sop.estimated_duration || 30,
        skill_level: sop.skill_level || "Intermediate",
      });
    }
  }, [sop]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryRef.current && !categoryRef.current.contains(event.target)) {
        setIsCategoryOpen(false);
      }
      if (skillRef.current && !skillRef.current.contains(event.target)) {
        setIsSkillOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleStartEditing = () => {
    if (sop) {
      setEditData({
        category: sop.category || "Operation",
        estimated_duration: sop.estimated_duration || 30,
        skill_level: sop.skill_level || "Intermediate",
      });
    }
    setIsEditing(true);
  };

  const handleCancelEditing = () => {
    if (sop) {
      setEditData({
        category: sop.category || "Operation",
        estimated_duration: sop.estimated_duration || 30,
        skill_level: sop.skill_level || "Intermediate",
      });
    }
    setIsEditing(false);
    setIsCategoryOpen(false);
    setIsSkillOpen(false);
  };

  // Mutations
  const approveMutation = useMutation({
    mutationFn: () => sopsAPI.updateStatus(id, "approved"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sop", id] });
      queryClient.invalidateQueries({ queryKey: ["sops"] });
      toast.success("SOP approved and published successfully");
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || "Failed to approve SOP");
    },
  });

  const updateMetadataMutation = useMutation({
    mutationFn: (data) => sopsAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sop", id] });
      queryClient.invalidateQueries({ queryKey: ["sops"] });
      setIsEditing(false);
      toast.success("Metadata updated successfully");
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || "Failed to update metadata");
    },
  });

  const logMutation = useMutation({
    mutationFn: (data) => maintenanceAPI.generateAiLog(data),
    onSuccess: () => {
      toast.success("Work completed and maintenance log filed.");
      navigate("/maintenance");
    },
    onError: (err) => toast.error("Failed to file log: " + err.message),
  });

  // Photo Handling
  const handleCapture = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("image", file);

    setIsUploading(true);
    try {
      const res = await maintenanceAPI.uploadImage(formData);
      setCapturedPhoto(res.data.url);
      toast.success("Photo attached successfully");
    } catch (err) {
      toast.error("Failed to upload image");
      console.error(err);
    } finally {
      setIsUploading(false);
      e.target.value = "";
    }
  };

  const removePhoto = () => {
    setCapturedPhoto(null);
    if (completedSteps[currentStep]?.photoUrl) {
      setCompletedSteps((prev) => ({
        ...prev,
        [currentStep]: {
          ...prev[currentStep],
          photoUrl: null,
          photoRemoved: true,
        },
      }));
    }
    toast.success("Photo removed");
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-3">
        <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
        <p className="text-xs sm:text-sm text-slate-500 font-medium">Loading procedure details...</p>
      </div>
    );
  }

  if (!sop) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs p-12 text-center max-w-lg mx-auto">
        <div className="w-12 h-12 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center mx-auto mb-3">
          <AlertTriangle className="w-6 h-6" />
        </div>
        <h3 className="text-base font-bold text-slate-900 mb-1">Procedure Not Found</h3>
        <p className="text-xs text-slate-500 mb-5">
          The procedure you are looking for does not exist or has been deleted.
        </p>
        <Link to="/sops" className="btn btn-secondary text-xs px-4 py-2">
          Back to Procedures
        </Link>
      </div>
    );
  }

  const steps = sop.steps || [];

  const isCurrentStepDone = Boolean(completedSteps[currentStep]);
  const currentStepNotes =
    notes[currentStep] !== undefined
      ? notes[currentStep]
      : completedSteps[currentStep]?.notes || "";
  const savedStepNotes = completedSteps[currentStep]?.notes || "";
  const isNotesChanged = currentStepNotes !== savedStepNotes;
  const activeStepPhoto =
    capturedPhoto ||
    (completedSteps[currentStep]?.photoRemoved
      ? null
      : completedSteps[currentStep]?.photoUrl);
  const isPhotoChanged =
    Boolean(capturedPhoto) || Boolean(completedSteps[currentStep]?.photoRemoved);
  const isStepModified = isNotesChanged || isPhotoChanged;

  const isStepDone = (i) => {
    if (!completedSteps[i]) return false;
    if (i === currentStep && isStepModified) return false;
    return true;
  };

  const completedCount = steps.filter((_, i) => isStepDone(i)).length;
  const progress = steps.length > 0 ? Math.round((completedCount / steps.length) * 100) : 0;

  const safetyWarnings = (
    Array.isArray(sop.safety_warnings)
      ? sop.safety_warnings
      : typeof sop.safety_warnings === "string"
        ? [sop.safety_warnings]
        : []
  ).filter((w) => typeof w === "string" && w.trim().length > 0);

  const requiredTools = (
    Array.isArray(sop.required_tools)
      ? sop.required_tools
      : typeof sop.required_tools === "string"
        ? [sop.required_tools]
        : []
  ).filter((t) => typeof t === "string" && t.trim().length > 0);

  const isStepAccessible = (index) => {
    if (index === 0) return true;
    return Boolean(completedSteps[index - 1]);
  };

  const handleCompleteStep = (stepIndex) => {
    const step = steps[stepIndex];
    const hasPhoto =
      capturedPhoto ||
      (completedSteps[stepIndex]?.photoUrl && !completedSteps[stepIndex]?.photoRemoved);
    if (step?.requiresImage && !hasPhoto) {
      toast.error("A photo is required for this step before continuing.");
      return;
    }
    const finalNote =
      notes[stepIndex] !== undefined
        ? notes[stepIndex]
        : completedSteps[stepIndex]?.notes || "";

    setCompletedSteps((prev) => {
      const updated = {
        ...prev,
        [stepIndex]: {
          completedAt: prev[stepIndex]?.completedAt || new Date(),
          photoUrl:
            capturedPhoto ||
            (prev[stepIndex]?.photoRemoved ? null : prev[stepIndex]?.photoUrl) ||
            null,
          notes: finalNote,
        },
      };
      delete updated[stepIndex].photoRemoved;
      return updated;
    });
    setCapturedPhoto(null);
    if (stepIndex < steps.length - 1) {
      setCurrentStep(stepIndex + 1);
    } else {
      setIsFinalizing(true);
    }
  };

  const handleFinishAndFile = () => {
    if (!finalSummary.trim()) {
      toast.error("Please provide a final summary of the work done.");
      return;
    }

    const executionDataArray = steps.map((s, i) => ({
      stepIndex: i,
      instruction: s.instruction,
      notes: completedSteps[i]?.notes || notes[i] || "",
      photoUrl: completedSteps[i]?.photoUrl || null,
      completedAt: completedSteps[i]?.completedAt || new Date(),
    }));

    logMutation.mutate({
      logId: null,
      rawNotes: finalSummary,
      machine_id: sop.machine_id,
      sop_id: id,
      executionData: executionDataArray,
    });
  };

  return (
    <div className="space-y-5 lg:space-y-6 w-full">
      {/* Top Header matching Dashboard, Machines & SOPs */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-2 border-b border-slate-200/80">
        <div className="flex items-center gap-3 sm:gap-3.5 min-w-0">
          <Link
            to="/sops"
            className="p-2 sm:p-2.5 bg-white hover:bg-slate-50 text-slate-600 hover:text-slate-900 border border-slate-200/90 rounded-xl shadow-xs transition-colors shrink-0"
            title="Back to procedures list"
          >
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          </Link>

          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 sm:gap-2.5">
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight truncate">
                {sop.title}
              </h1>
              {sop.is_ai_generated && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200/80">
                  <Sparkles className="w-3 h-3 text-indigo-600" />
                  AI Generated
                </span>
              )}
              <span
                className={clsx(
                  "inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border",
                  currentStatus.badge
                )}
              >
                <span className={clsx("w-1.5 h-1.5 rounded-full", currentStatus.dot)} />
                <span>{currentStatus.label}</span>
              </span>
            </div>

            <div className="flex items-center gap-2 text-xs text-slate-500 mt-1 truncate">
              <span className="flex items-center gap-1 font-medium text-slate-600">
                <Cpu className="w-3.5 h-3.5 text-slate-400" />
                {sop.machine_name || "General Machinery"}
              </span>
              <span className="text-slate-300">•</span>
              <span>{sop.category || "General"}</span>
              {sop.version && (
                <>
                  <span className="text-slate-300">•</span>
                  <span>v{sop.version}</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2.5 flex-wrap sm:flex-nowrap">

          {isAdmin && isDraft && !isExecuting && (
            <button
              type="button"
              onClick={() => approveMutation.mutate()}
              disabled={approveMutation.isPending}
              className="btn bg-emerald-600 hover:bg-emerald-700 text-white text-xs sm:text-sm py-2 px-3.5 sm:px-4 shadow-md shadow-emerald-600/20 inline-flex items-center gap-1.5 font-medium"
            >
              {approveMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4" />
              )}
              <span>Approve & Publish</span>
            </button>
          )}

          {!isExecuting && sopStatus === "approved" && (
            <button
              type="button"
              onClick={() => {
                setIsExecuting(true);
                setCurrentStep(0);
                setCompletedSteps({});
                setIsFinalizing(false);
              }}
              className="btn btn-primary text-xs sm:text-sm py-2 px-3.5 sm:px-4 shadow-md shadow-blue-500/20 inline-flex items-center gap-1.5"
            >
              <Play className="w-4 h-4" />
              <span>Start Procedure</span>
            </button>
          )}

          {isExecuting && (
            <button
              type="button"
              onClick={() => setIsExecuting(false)}
              className="btn btn-secondary text-xs sm:text-sm py-2 px-3.5 hover:border-slate-400 inline-flex items-center gap-1.5"
            >
              <RotateCcw className="w-3.5 h-3.5 text-slate-500" />
              <span>Exit Execution</span>
            </button>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      {isExecuting ? (
        /* EXECUTION MODE - REDESIGNED INDUSTRIAL CONTROL THEME */
        <div className="space-y-5 lg:space-y-6">
          {/* Top Progress & Command Bar */}
          <div className="bg-white border border-slate-200/80 rounded-2xl shadow-sm p-4 sm:p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="space-y-1.5">
              <div className="flex items-center gap-2.5 flex-wrap">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200/80">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-600"></span>
                  </span>
                  Live Execution Active
                </span>
                <span className="text-xs font-semibold text-slate-700 truncate max-w-md">
                  {sop.title}
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <span className="flex items-center gap-1 font-medium text-slate-700">
                  <Cpu className="w-3.5 h-3.5 text-slate-400" />
                  {sop.machine_name || "General Machinery"}
                </span>
                <span className="text-slate-300">•</span>
                <span>Follow instructions in order. Checkpoints and photo verifications required.</span>
              </div>
            </div>

            {/* Progress tracker widget */}
            <div className="flex items-center gap-4 min-w-[260px] bg-slate-50/90 p-3 rounded-xl border border-slate-200/70">
              <div className="flex-1">
                <div className="flex justify-between text-xs font-bold text-slate-700 mb-1.5">
                  <span className="text-slate-500 font-medium">Execution Progress</span>
                  <span className="text-blue-600 font-bold">{progress}%</span>
                </div>
                <div className="w-full h-2 bg-slate-200/80 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
              <div className="text-right shrink-0 border-l border-slate-200 pl-3">
                <span className="text-xs font-extrabold text-slate-900 block">
                  {completedCount} / {steps.length}
                </span>
                <p className="text-[10px] text-slate-400 font-medium">Steps Done</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-6 items-start">
            {/* Steps Checklist Sidebar (4 cols) */}
            <div className="lg:col-span-4 bg-white border border-slate-200/80 rounded-2xl shadow-sm p-4 sm:p-5 space-y-3 lg:sticky lg:top-6">
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                    <FileText className="w-3.5 h-3.5" />
                  </div>
                  <h4 className="text-xs sm:text-sm font-bold text-slate-900">
                    Step Checklist
                  </h4>
                </div>
                <span className="text-xs font-bold px-2 py-0.5 rounded-md bg-slate-100 text-slate-600">
                  {completedCount}/{steps.length} done
                </span>
              </div>

              <div className="space-y-2 max-h-[540px] overflow-y-auto pr-1">
                {steps.map((s, i) => {
                  const isDone = isStepDone(i);
                  const isCurrent = currentStep === i && !isFinalizing;
                  const isLocked = !isStepAccessible(i);
                  return (
                    <button
                      key={i}
                      type="button"
                      disabled={isLocked}
                      onClick={() => {
                        if (isLocked) {
                          toast.error("Please complete the previous step first.");
                          return;
                        }
                        setCapturedPhoto(null);
                        setCurrentStep(i);
                        setIsFinalizing(false);
                      }}
                      className={clsx(
                        "w-full text-left p-3 rounded-xl text-xs sm:text-sm flex gap-3 items-start transition-all border relative",
                        isLocked
                          ? "bg-slate-50/50 border-slate-200/50 text-slate-400 cursor-not-allowed opacity-60"
                          : isCurrent
                            ? "bg-blue-50/90 border-blue-500/80 text-blue-950 font-medium shadow-sm ring-1 ring-blue-500/20"
                            : isDone
                              ? "bg-emerald-50/50 border-emerald-200/80 text-slate-800 hover:bg-emerald-50/80 cursor-pointer"
                              : "bg-white border-slate-200/80 text-slate-600 hover:bg-slate-50 cursor-pointer"
                      )}
                      title={isLocked ? "Complete previous step to unlock" : undefined}
                    >
                      {/* Step Indicator Avatar */}
                      <div
                        className={clsx(
                          "w-6 h-6 rounded-lg flex items-center justify-center text-[11px] font-bold shrink-0 transition-all mt-0.5",
                          isLocked
                            ? "bg-slate-100 text-slate-400 border border-slate-200/70"
                            : isDone
                              ? "bg-emerald-600 text-white shadow-xs"
                              : isCurrent
                                ? "bg-blue-600 text-white shadow-xs shadow-blue-600/30"
                                : "bg-slate-100 text-slate-500 border border-slate-200"
                        )}
                      >
                        {isLocked ? (
                          <Lock className="w-3 h-3 text-slate-400" />
                        ) : isDone ? (
                          <Check className="w-3.5 h-3.5 stroke-[2.5]" />
                        ) : (
                          i + 1
                        )}
                      </div>

                      <div className="min-w-0 flex-1">
                        <p
                          className={clsx(
                            "text-xs leading-snug line-clamp-2",
                            isLocked
                              ? "text-slate-400"
                              : isCurrent
                                ? "font-bold text-blue-950"
                                : isDone
                                  ? "font-semibold text-slate-800"
                                  : "font-medium text-slate-600"
                          )}
                        >
                          {s.instruction || `Step ${i + 1}`}
                        </p>
                        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                          {s.checkPoint && (
                            <span
                              className={clsx(
                                "inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded",
                                isLocked
                                  ? "bg-slate-100 text-slate-400 border border-slate-200/50"
                                  : "bg-amber-50 text-amber-800 border border-amber-200/60"
                              )}
                            >
                              <Flag className="w-2.5 h-2.5 text-amber-600" />
                              Checkpoint
                            </span>
                          )}
                          {s.requiresImage && (
                            <span
                              className={clsx(
                                "inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded",
                                isLocked
                                  ? "bg-slate-100 text-slate-400 border border-slate-200/50"
                                  : "bg-blue-50 text-blue-800 border border-blue-200/60"
                              )}
                            >
                              <Camera className="w-2.5 h-2.5 text-blue-600" />
                              Photo
                            </span>
                          )}
                          {isDone && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 ml-auto">
                              <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                              Done
                            </span>
                          )}
                          {isLocked && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-slate-400 ml-auto">
                              <Lock className="w-2.5 h-2.5" />
                              Locked
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Main Interactive Step Area (8 cols) */}
            <div className="lg:col-span-8">
              <AnimatePresence mode="wait">
                {!isFinalizing ? (
                  <motion.div
                    key={currentStep}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="bg-white border border-slate-200/80 rounded-2xl shadow-sm p-6 sm:p-7 space-y-6"
                  >
                    {/* Step Header Banner */}
                    <div className="flex items-center justify-between pb-4 border-b border-slate-100 flex-wrap gap-2.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center px-3 py-1 rounded-xl text-xs font-bold bg-blue-600 text-white shadow-xs tracking-wide">
                          Step {currentStep + 1} of {steps.length}
                        </span>
                        {isStepDone(currentStep) ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Completed
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-xs font-semibold bg-slate-100 text-slate-600 border border-slate-200/80">
                            <Clock className="w-3.5 h-3.5 text-slate-400" /> In Progress
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-2 flex-wrap">
                        {steps[currentStep]?.checkPoint && (
                          <span className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-xl bg-amber-50 text-amber-800 border border-amber-200/80 shadow-2xs">
                            <Flag className="w-3.5 h-3.5 text-amber-600" />
                            Checkpoint
                          </span>
                        )}
                        {steps[currentStep]?.requiresImage && (
                          <span className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-xl bg-blue-50 text-blue-800 border border-blue-200/80 shadow-2xs">
                            <Camera className="w-3.5 h-3.5 text-blue-600" />
                            Photo Required
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Instruction Headline */}
                    <div className="space-y-1.5">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">
                        Instruction
                      </span>
                      <h2 className="text-xl sm:text-2xl font-bold text-slate-900 leading-snug tracking-tight">
                        {steps[currentStep]?.instruction}
                      </h2>
                    </div>

                    {/* Caution / Hazard Alert */}
                    {steps[currentStep]?.warning && (
                      <div className="py-2.5 px-3.5 bg-rose-50/70 border border-rose-200/80 rounded-xl flex items-start gap-2.5 shadow-2xs">
                        <div className="w-6 h-6 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center shrink-0 mt-0.5">
                          <AlertTriangle className="w-3.5 h-3.5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="text-[11px] font-bold text-rose-900 uppercase tracking-wider">
                            Safety Caution
                          </h4>
                          <p className="text-xs text-rose-800 font-medium leading-relaxed mt-0.5">
                            {steps[currentStep].warning}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Step Notes Input */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                          <FileText className="w-3.5 h-3.5 text-slate-400" />
                          Observation & Measurement Notes
                        </label>
                        <span className="text-[11px] text-slate-400 font-medium">Optional</span>
                      </div>
                      <textarea
                        className="w-full px-4 py-3 bg-slate-50/60 hover:bg-slate-50 focus:bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 shadow-2xs transition-all min-h-[90px] resize-y"
                        placeholder="Record values, torque readings, tool offsets, or any abnormalities observed..."
                        value={currentStepNotes}
                        onChange={(e) =>
                          setNotes({ ...notes, [currentStep]: e.target.value })
                        }
                      />
                    </div>

                    {/* Photographic Evidence Attachment */}
                    <div className="p-4 sm:p-5 bg-slate-50/70 border border-slate-200/80 rounded-2xl space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                            <Camera className="w-4 h-4" />
                          </div>
                          <h4 className="text-xs sm:text-sm font-bold text-slate-900">
                            Photo Verification
                          </h4>
                        </div>
                        {steps[currentStep]?.requiresImage ? (
                          <span className="text-[11px] font-bold text-rose-600 bg-rose-50 px-2.5 py-0.5 rounded-full border border-rose-200/60">
                            Required to Advance
                          </span>
                        ) : (
                          <span className="text-[11px] font-medium text-slate-400">
                            Optional Evidence
                          </span>
                        )}
                      </div>

                      {!activeStepPhoto ? (
                        <div>
                          <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            id="sop-camera-input"
                            className="hidden"
                            onChange={handleCapture}
                            disabled={isUploading}
                          />
                          <label
                            htmlFor="sop-camera-input"
                            className={clsx(
                              "w-full py-5 px-4 border-2 border-dashed rounded-xl cursor-pointer flex flex-col items-center justify-center gap-2 transition-all group",
                              isUploading
                                ? "bg-slate-100 text-slate-400 border-slate-300 cursor-not-allowed"
                                : "bg-white border-slate-200 hover:border-blue-400 hover:bg-blue-50/30 shadow-2xs"
                            )}
                          >
                            {isUploading ? (
                              <div className="flex items-center gap-2 text-xs sm:text-sm font-semibold text-blue-600">
                                <Loader2 className="w-5 h-5 animate-spin" />
                                <span>Uploading verification photo...</span>
                              </div>
                            ) : (
                              <>
                                <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 group-hover:scale-105 transition-transform flex items-center justify-center shadow-2xs">
                                  <Camera className="w-5 h-5" />
                                </div>
                                <div className="text-center">
                                  <p className="text-xs sm:text-sm font-bold text-slate-800 group-hover:text-blue-600 transition-colors">
                                    Attach / Capture Verification Photo
                                  </p>
                                  <p className="text-[11px] text-slate-400 mt-0.5">
                                    Click to capture with device camera or upload image
                                  </p>
                                </div>
                              </>
                            )}
                          </label>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between p-3.5 bg-white border border-emerald-200 rounded-xl shadow-xs">
                          <div className="flex items-center gap-3.5 min-w-0">
                            <img
                              src={activeStepPhoto}
                              alt="Captured verification"
                              className="w-16 h-16 object-cover rounded-xl border border-slate-200 shrink-0 shadow-2xs"
                            />
                            <div className="min-w-0">
                              <p className="text-xs font-bold text-emerald-800 flex items-center gap-1.5">
                                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                                Photo Verified
                              </p>
                              <p className="text-[11px] text-slate-500 mt-0.5">
                                Attached to execution log
                              </p>
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={removePhoto}
                            className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-colors border border-transparent hover:border-rose-100"
                            title="Remove photo"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Step Navigation Controls */}
                    <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100 flex-wrap">
                      <button
                        type="button"
                        onClick={() => {
                          setCapturedPhoto(null);
                          setCurrentStep((c) => Math.max(0, c - 1));
                        }}
                        disabled={currentStep === 0}
                        className="btn btn-secondary text-xs sm:text-sm py-2.5 px-4 shadow-xs disabled:opacity-40 rounded-xl inline-flex items-center gap-1.5"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        <span>Previous</span>
                      </button>

                      {isCurrentStepDone && !isStepModified ? (
                        <button
                          type="button"
                          onClick={() => {
                            setCapturedPhoto(null);
                            if (currentStep < steps.length - 1) {
                              setCurrentStep(currentStep + 1);
                            } else {
                              setIsFinalizing(true);
                            }
                          }}
                          disabled={isUploading}
                          className="btn btn-primary text-xs sm:text-sm py-2.5 px-5 shadow-md shadow-blue-500/20 inline-flex items-center gap-2 font-semibold rounded-xl"
                        >
                          <span>Next</span>
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleCompleteStep(currentStep)}
                          disabled={
                            isUploading ||
                            (!activeStepPhoto && steps[currentStep]?.requiresImage)
                          }
                          className="btn btn-primary text-xs sm:text-sm py-2.5 px-5 shadow-md shadow-blue-500/20 inline-flex items-center gap-2 font-semibold rounded-xl"
                        >
                          <span>
                            {currentStep === steps.length - 1
                              ? "Save & Review Log"
                              : "Save & Next"}
                          </span>
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </motion.div>
                ) : (
                  /* Finalize Work Log View */
                  <motion.div
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.2 }}
                    className="bg-white border border-slate-200/80 rounded-2xl shadow-sm p-6 sm:p-8 space-y-6"
                  >
                    <div className="flex items-start justify-between pb-5 border-b border-slate-100 gap-4">
                      <div className="flex items-center gap-3.5">
                        <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 border border-emerald-100 flex items-center justify-center shrink-0 shadow-xs">
                          <CheckCircle2 className="w-6 h-6" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-lg sm:text-xl font-bold text-slate-900">
                              Procedure Steps Completed
                            </h3>
                            <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800">
                              100%
                            </span>
                          </div>
                          <p className="text-xs sm:text-sm text-slate-500 mt-1">
                            All {steps.length} procedural steps have been executed. Review notes and submit sign-off to file the maintenance log.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Summary Stats Pill Grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/80">
                        <span className="text-[10px] font-bold uppercase text-slate-400 block tracking-wider">Steps Completed</span>
                        <span className="text-base sm:text-lg font-bold text-slate-900 mt-0.5 block">{completedCount} / {steps.length}</span>
                      </div>
                      <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/80">
                        <span className="text-[10px] font-bold uppercase text-slate-400 block tracking-wider">Photos Logged</span>
                        <span className="text-base sm:text-lg font-bold text-slate-900 mt-0.5 block">
                          {Object.values(completedSteps).filter((s) => s?.photoUrl).length} Photos
                        </span>
                      </div>
                      <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/80 col-span-2 sm:col-span-1">
                        <span className="text-[10px] font-bold uppercase text-slate-400 block tracking-wider">Equipment</span>
                        <span className="text-xs sm:text-sm font-bold text-slate-900 mt-0.5 block truncate">
                          {sop.machine_name || "General Machinery"}
                        </span>
                      </div>
                    </div>

                    {/* Work Summary Input */}
                    <div className="space-y-2">
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                        Work Summary & Observations <span className="text-rose-500">*</span>
                      </label>
                      <textarea
                        className="w-full px-4 py-3 bg-slate-50/60 hover:bg-slate-50 focus:bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 shadow-2xs transition-all min-h-[130px] resize-y"
                        placeholder="e.g. Successfully inspected spindle alignment, cleaned guide rails, and replaced pneumatic filters. Equipment restarted within nominal vibration tolerance."
                        value={finalSummary}
                        onChange={(e) => setFinalSummary(e.target.value)}
                      />
                    </div>

                    {/* Final Actions */}
                    <div className="flex items-center justify-end pt-3 border-t border-slate-100 gap-3">
                      <button
                        type="button"
                        onClick={() => setIsFinalizing(false)}
                        className="btn btn-secondary text-xs sm:text-sm py-2.5 px-4 rounded-xl shadow-xs inline-flex items-center gap-1.5"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        <span>Review Steps</span>
                      </button>

                      <button
                        type="button"
                        onClick={handleFinishAndFile}
                        disabled={logMutation.isPending || !finalSummary.trim()}
                        className="btn btn-primary text-xs sm:text-sm py-2.5 px-5 rounded-xl shadow-md shadow-blue-500/20 inline-flex items-center gap-2 font-semibold"
                      >
                        {logMutation.isPending ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span>Filing Log with AI...</span>
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4" />
                            <span>Sign Off & File Maintenance Log</span>
                          </>
                        )}
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      ) : (
        /* STANDARD OVERVIEW MODE (FULL WIDTH 12 COLS) */
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-6 items-start">
          {/* Main Left Column: Overview & Procedure Steps (8 cols) */}
          <div className="lg:col-span-8 space-y-5 lg:space-y-6">
            {/* Overview / Description Card */}
            <div className="bg-white border border-slate-200/80 rounded-2xl shadow-sm p-5 sm:p-6 space-y-3.5">
              <div className="flex items-center gap-2.5 pb-3 border-b border-slate-100">
                <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                  <FileText className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-sm sm:text-base font-bold text-slate-900">
                    Procedure Scope & Description
                  </h3>
                  <p className="text-xs text-slate-500">
                    Operational guidelines, purpose, and prerequisites
                  </p>
                </div>
              </div>

              <div className="text-xs sm:text-sm text-slate-700 leading-relaxed whitespace-pre-line">
                {sop.description || (
                  <span className="text-slate-400 italic">
                    No detailed description provided for this procedure.
                  </span>
                )}
              </div>
            </div>

            {/* Step-by-Step Procedure Card */}
            <div className="bg-white border border-slate-200/80 rounded-2xl shadow-sm p-5 sm:p-6 space-y-4">
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                    <Layers className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-sm sm:text-base font-bold text-slate-900">
                      Sequential Instructions
                    </h3>
                    <p className="text-xs text-slate-500">
                      {steps.length} sequential execution steps
                    </p>
                  </div>
                </div>

                <span className="text-xs font-semibold text-slate-500 bg-slate-100 px-2.5 py-1 rounded-lg">
                  {steps.length} Steps
                </span>
              </div>

              <div className="space-y-3">
                {steps.map((step, i) => (
                  <div
                    key={i}
                    className="p-4 bg-slate-50/70 border border-slate-200/90 rounded-2xl space-y-2.5 transition-all hover:bg-slate-50"
                  >
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-600 text-white shadow-xs">
                          Step {i + 1}
                        </span>
                        {step.checkPoint && (
                          <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-md bg-amber-50 text-amber-800 border border-amber-200">
                            <Flag className="w-3 h-3 text-amber-600" />
                            Checkpoint
                          </span>
                        )}
                        {step.requiresImage && (
                          <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-md bg-blue-50 text-blue-800 border border-blue-200">
                            <Camera className="w-3 h-3 text-blue-600" />
                            Photo Required
                          </span>
                        )}
                      </div>
                    </div>

                    <p className="text-xs sm:text-sm font-semibold text-slate-800 leading-normal">
                      {step.instruction}
                    </p>

                    {step.warning && (
                      <div className="p-3 bg-rose-50/90 border border-rose-200/90 rounded-xl flex items-start gap-2.5 mt-2">
                        <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                        <p className="text-xs text-rose-800 font-medium leading-relaxed">
                          <span className="font-bold text-rose-900">CAUTION: </span>
                          {step.warning}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column: Metadata, Safety & Tools (4 cols) */}
          <div className="lg:col-span-4 space-y-5 lg:space-y-6">
            {/* Quick Specs / Metadata Card */}
            <div className="bg-white border border-slate-200/80 rounded-2xl shadow-sm p-5 space-y-4">
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                    <Award className="w-4 h-4" />
                  </div>
                  <h3 className="text-xs sm:text-sm font-bold text-slate-900">
                    Procedure Specs
                  </h3>
                </div>

                <div className="flex items-center gap-2">
                  <span
                    className={clsx(
                      "inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border",
                      currentStatus.badge
                    )}
                  >
                    <span className={clsx("w-1.5 h-1.5 rounded-full", currentStatus.dot)} />
                    <span>{currentStatus.label}</span>
                  </span>

                  {!isExecuting && !isEditing && (
                    <button
                      type="button"
                      onClick={handleStartEditing}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-slate-600 hover:text-blue-600 bg-slate-50 hover:bg-blue-50 border border-slate-200 hover:border-blue-200 rounded-lg transition-colors shadow-2xs"
                      title="Edit Procedure Specs"
                    >
                      <Edit2 className="w-3 h-3 text-slate-500" />
                      <span>Edit</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Metadata Display / Edit List - Keeping the exact card design */}
              <div className="divide-y divide-slate-100 text-xs">
                {/* Category Row */}
                <div className="flex justify-between items-center py-2.5 gap-2">
                  <span className="text-slate-500 font-medium flex items-center gap-1.5 shrink-0">
                    <Layers className="w-3.5 h-3.5 text-slate-400" /> Category
                  </span>
                  {isEditing ? (
                    <div className="relative" ref={categoryRef}>
                      <button
                        type="button"
                        onClick={() => {
                          setIsCategoryOpen(!isCategoryOpen);
                          setIsSkillOpen(false);
                        }}
                        className="w-36 sm:w-40 pl-3 pr-2.5 py-1.5 bg-slate-50/70 hover:bg-slate-50 focus:bg-white border border-slate-200 rounded-xl text-xs text-slate-800 flex items-center justify-between transition-all shadow-2xs focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                      >
                        <div className="flex items-center gap-2 truncate">
                          <span
                            className={clsx(
                              "w-2 h-2 rounded-full shrink-0",
                              getCategoryConfig(editData?.category).dot
                            )}
                          />
                          <span className="font-medium text-slate-800 truncate">
                            {getCategoryConfig(editData?.category).label}
                          </span>
                        </div>
                        <ChevronDown
                          className={clsx(
                            "w-3.5 h-3.5 text-slate-400 shrink-0 transition-transform duration-200",
                            isCategoryOpen && "rotate-180"
                          )}
                        />
                      </button>

                      <AnimatePresence>
                        {isCategoryOpen && (
                          <motion.div
                            initial={{ opacity: 0, y: -6, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -6, scale: 0.98 }}
                            transition={{ duration: 0.15 }}
                            className="absolute right-0 z-30 mt-1.5 w-44 rounded-xl bg-white shadow-xl border border-slate-200 py-1.5"
                          >
                            {Object.entries(categoryOptions).map(([key, config]) => (
                              <button
                                type="button"
                                key={key}
                                onClick={() => {
                                  setEditData({ ...editData, category: key });
                                  setIsCategoryOpen(false);
                                }}
                                className={clsx(
                                  "flex items-center justify-between w-full px-3 py-2 text-xs text-left transition-colors font-medium",
                                  (editData?.category || "").toLowerCase() === key.toLowerCase()
                                    ? "bg-blue-50 text-blue-700 font-semibold"
                                    : "text-slate-700 hover:bg-slate-50"
                                )}
                              >
                                <div className="flex items-center gap-2">
                                  <span className={clsx("w-2 h-2 rounded-full shrink-0", config.dot)} />
                                  <span>{config.label}</span>
                                </div>
                                {(editData?.category || "").toLowerCase() === key.toLowerCase() && (
                                  <span className="w-1.5 h-1.5 rounded-full bg-blue-600 shrink-0" />
                                )}
                              </button>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <span className="font-semibold text-slate-800">
                      {sop.category || "General"}
                    </span>
                  )}
                </div>

                {/* Est. Duration Row */}
                <div className="flex justify-between items-center py-2.5 gap-2">
                  <span className="text-slate-500 font-medium flex items-center gap-1.5 shrink-0">
                    <Clock className="w-3.5 h-3.5 text-slate-400" /> Est. Duration
                  </span>
                  {isEditing ? (
                    <div className="flex items-center gap-1.5">
                      <input
                        type="number"
                        min="1"
                        value={editData?.estimated_duration || ""}
                        onChange={(e) =>
                          setEditData({
                            ...editData,
                            estimated_duration: parseInt(e.target.value) || 0,
                          })
                        }
                        className="w-20 px-2.5 py-1.5 text-right font-medium text-xs text-slate-800 bg-slate-50/70 hover:bg-slate-50 focus:bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-2xs"
                      />
                      <span className="text-xs text-slate-500 font-medium">mins</span>
                    </div>
                  ) : (
                    <span className="font-semibold text-slate-800">
                      {sop.estimated_duration ? `${sop.estimated_duration} mins` : "Flexible"}
                    </span>
                  )}
                </div>

                {/* Skill Level Row */}
                <div className="flex justify-between items-center py-2.5 gap-2">
                  <span className="text-slate-500 font-medium flex items-center gap-1.5 shrink-0">
                    <Award className="w-3.5 h-3.5 text-slate-400" /> Skill Level
                  </span>
                  {isEditing ? (
                    <div className="relative" ref={skillRef}>
                      <button
                        type="button"
                        onClick={() => {
                          setIsSkillOpen(!isSkillOpen);
                          setIsCategoryOpen(false);
                        }}
                        className="w-36 sm:w-40 pl-3 pr-2.5 py-1.5 bg-slate-50/70 hover:bg-slate-50 focus:bg-white border border-slate-200 rounded-xl text-xs text-slate-800 flex items-center justify-between transition-all shadow-2xs focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                      >
                        <div className="flex items-center gap-2 truncate">
                          <span
                            className={clsx(
                              "w-2 h-2 rounded-full shrink-0",
                              getSkillConfig(editData?.skill_level).dot
                            )}
                          />
                          <span className="font-medium text-slate-800 truncate">
                            {getSkillConfig(editData?.skill_level).label}
                          </span>
                        </div>
                        <ChevronDown
                          className={clsx(
                            "w-3.5 h-3.5 text-slate-400 shrink-0 transition-transform duration-200",
                            isSkillOpen && "rotate-180"
                          )}
                        />
                      </button>

                      <AnimatePresence>
                        {isSkillOpen && (
                          <motion.div
                            initial={{ opacity: 0, y: -6, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -6, scale: 0.98 }}
                            transition={{ duration: 0.15 }}
                            className="absolute right-0 z-30 mt-1.5 w-52 rounded-xl bg-white shadow-xl border border-slate-200 py-1.5"
                          >
                            {Object.entries(skillLevelOptions).map(([key, config]) => (
                              <button
                                type="button"
                                key={key}
                                onClick={() => {
                                  setEditData({ ...editData, skill_level: key });
                                  setIsSkillOpen(false);
                                }}
                                className={clsx(
                                  "flex items-center justify-between w-full px-3 py-2 text-xs text-left transition-colors font-medium",
                                  (editData?.skill_level || "").toLowerCase() === key.toLowerCase()
                                    ? "bg-blue-50 text-blue-700 font-semibold"
                                    : "text-slate-700 hover:bg-slate-50"
                                )}
                              >
                                <div className="flex items-center gap-2">
                                  <span className={clsx("w-2 h-2 rounded-full shrink-0", config.dot)} />
                                  <div>
                                    <span className="block">{config.label}</span>
                                    {config.sublabel && (
                                      <span className="block text-[10px] text-slate-400 font-normal">
                                        {config.sublabel}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {(editData?.skill_level || "").toLowerCase() === key.toLowerCase() && (
                                  <span className="w-1.5 h-1.5 rounded-full bg-blue-600 shrink-0" />
                                )}
                              </button>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <span className="font-semibold text-slate-800 capitalize">
                      {sop.skill_level ? sop.skill_level.replace(/_/g, " ") : "Intermediate"}
                    </span>
                  )}
                </div>

                {/* Machinery Row - Full text visible without truncation */}
                <div className="flex justify-between items-start py-2.5 gap-3">
                  <span className="text-slate-500 font-medium flex items-center gap-1.5 shrink-0 pt-0.5">
                    <Cpu className="w-3.5 h-3.5 text-slate-400" /> Machinery
                  </span>
                  <span className="font-semibold text-slate-800 text-right leading-snug break-words">
                    {sop.machine_name || "General Machinery"}
                  </span>
                </div>

                {/* Author Row */}
                {sop.created_by_name && (
                  <div className="flex justify-between items-center py-2.5">
                    <span className="text-slate-500 font-medium">Author</span>
                    <span className="font-semibold text-slate-800">
                      {sop.created_by_name}
                    </span>
                  </div>
                )}

                {/* Approved By Row */}
                {sop.approved_by_name && (
                  <div className="flex justify-between items-center py-2.5">
                    <span className="text-slate-500 font-medium">Approved By</span>
                    <span className="font-semibold text-emerald-700">
                      {sop.approved_by_name}
                    </span>
                  </div>
                )}
              </div>

              {/* Action Buttons when editing */}
              {isEditing && (
                <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={handleCancelEditing}
                    className="btn btn-secondary text-xs py-1.5 px-3"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => updateMetadataMutation.mutate(editData)}
                    disabled={updateMetadataMutation.isPending}
                    className="btn btn-primary text-xs py-1.5 px-3.5 inline-flex items-center gap-1.5 shadow-sm shadow-blue-500/20"
                  >
                    {updateMetadataMutation.isPending ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Save className="w-3.5 h-3.5" />
                    )}
                    <span>Save Changes</span>
                  </button>
                </div>
              )}
            </div>

            {/* Safety Warnings & Hazards Card */}
            <div className="bg-white border border-slate-200/80 rounded-2xl shadow-sm p-5 space-y-3.5">
              <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
                <div className="w-7 h-7 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center">
                  <ShieldAlert className="w-4 h-4" />
                </div>
                <h3 className="text-xs sm:text-sm font-bold text-slate-900">
                  Safety Warnings & Hazards
                </h3>
              </div>

              {safetyWarnings.length > 0 ? (
                <div className="space-y-2">
                  {safetyWarnings.map((w, i) => (
                    <div
                      key={i}
                      className="flex items-start gap-2.5 p-2.5 rounded-xl bg-rose-50/70 border border-rose-200/70 text-xs text-rose-900"
                    >
                      <Shield className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                      <span className="font-medium leading-relaxed">{w}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-5 px-3 text-center rounded-xl bg-slate-50/60 border border-dashed border-slate-200">
                  <p className="text-xs text-slate-400 font-medium">No data found</p>
                </div>
              )}
            </div>

            {/* Required Tools & PPE Card */}
            <div className="bg-white border border-slate-200/80 rounded-2xl shadow-sm p-5 space-y-3.5">
              <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
                <div className="w-7 h-7 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                  <Wrench className="w-4 h-4" />
                </div>
                <h3 className="text-xs sm:text-sm font-bold text-slate-900">
                  Required Tools & PPE
                </h3>
              </div>

              {requiredTools.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {requiredTools.map((t, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium bg-slate-100 text-slate-800 border border-slate-200/80"
                    >
                      <Wrench className="w-3.5 h-3.5 text-blue-600" />
                      <span>{t}</span>
                    </span>
                  ))}
                </div>
              ) : (
                <div className="py-5 px-3 text-center rounded-xl bg-slate-50/60 border border-dashed border-slate-200">
                  <p className="text-xs text-slate-400 font-medium">No data found</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SOPViewer;
